import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InviteDialog, type InvitePreset } from "@/components/InviteDialog";
import { PageHeader } from "@/components/PageHeader";
import { TabStrip, type TabStripItem } from "@/components/TabStrip";
import { InlineError } from "@/components/QueryState";
import { describeAccessEvent, formatWhen, ROLE_LABEL } from "@/components/boo/access-copy";
import { TakeOwnershipDialog } from "@/components/boo/TakeOwnershipDialog";
import { HandoverSheet } from "@/components/grants/HandoverSheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { api, ApiError, type PeopleResponse, type PendingInvite, type HandleRef, type PersonHandle, type PersonRow, type PersonUpdate, type SpendPeopleLine } from "@/lib/api";
import { authClient, useSession } from "@/lib/auth-client";
import { useSuperAdminView } from "@/lib/use-superadmin-view";
import { relativeIso } from "@/lib/format";
import {
  canChangeLevel,
  chatGroups,
  chatSummary,
  type ChatSummary,
  consequence,
  customGroups,
  displayName,
  featureCopy,
  groupLabel,
  type LevelKey,
  levelLabel,
  levelOf,
  levelOptions,
  levelPatch,
  likelyMatch,
  LINKABLE_PROVIDERS,
  platformLabel,
  rawIdName,
} from "@/lib/people-levels";
import { activePeopleTab, matchesQuery, type Page, pageOf, type PeopleSearch, type PeopleTab, personSearchText } from "@/lib/people-tabs";
import { useOrgRole } from "@/lib/use-org-role";
import { peopleLineText, peopleLineTone, personKeyOf } from "@/lib/spend-limits";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ChevronDown, EllipsisVertical, Mail, MessageSquare, Search, UserPlus, Users, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { ChannelDirectoryDialog } from "../ChannelDirectoryDialog";
import { PLATFORM_LABEL } from "../channel-mode";
import { WhenBooAnswers } from "../WhenBooAnswers";
import { AddFromOrgDialog } from "./AddFromOrgDialog";
import { AdvancedSettings, GroupSettings, UpgradeNotices } from "./GroupSettings";
import { type LinkPicker, LinkPickerDialog } from "./LinkPickerDialog";
import { initials } from "./person-format";
import { GroupChips, LevelPicker, PlatformIcons } from "./PersonParts";
import { peopleKey, useRoles } from "./use-identity";

type Filter = "all" | "dashboard" | "chat" | "blocked" | "invited";

const FILTER_LABEL: Record<Filter, string> = {
  all: "Everyone",
  dashboard: "Dashboard",
  chat: "Chat only",
  blocked: "Blocked",
  invited: "Invited",
};

type ListItem = { type: "person"; person: PersonRow } | { type: "invite"; invite: PendingInvite };

const HISTORY_STEP = 50;

type Pending =
  | { kind: "level"; person: PersonRow; to: LevelKey }
  | { kind: "merge"; accounts: PersonRow[]; into: PersonRow }
  | { kind: "separate"; person: PersonRow; handle: PersonHandle }
  | { kind: "remove"; person: PersonRow };

export function PeopleV2Tab() {
  const params = useParams({ strict: false }) as { orgId?: string; containerId?: string };
  const containerId = params.containerId ?? "";
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const myUserId = session?.user.id;
  const isSuperAdmin = useSuperAdminView();
  const orgRole = useOrgRole();

  const people = useQuery({
    queryKey: peopleKey(containerId),
    queryFn: () => api.containers.people.list(containerId),
    enabled: !!containerId,
  });
  const roles = useRoles(containerId);
  // One read-only line per person: their own limit across all Boos (§8.2).
  const spendLines = useQuery({
    queryKey: ["spend-limits-people", containerId],
    queryFn: () => api.containers.spendLimits.people(containerId),
    enabled: !!containerId,
    retry: false,
  });
  const spendLineOf = new Map((spendLines.data?.data ?? []).map((r) => [r.personKey, r.line]));
  const channels = useQuery({
    queryKey: ["channels", containerId],
    queryFn: () => api.containers.channels.list(containerId),
    enabled: !!containerId,
  });
  const platforms = useQuery({
    queryKey: ["channel-platforms", containerId],
    queryFn: () => api.containers.channels.platforms(containerId),
    enabled: !!containerId,
  });
  const boos = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const boo = boos.data?.data.find((b) => b.id === containerId);
  const orgPerms = useQuery({ queryKey: ["org-permissions", params.orgId], queryFn: () => api.org.permissions() });
  const events = useQuery({
    queryKey: ["boo-access-events", containerId],
    queryFn: () => api.containers.members.events(containerId),
    enabled: !!containerId,
  });

  const perms = people.data?._permissions;
  const rolePerms = perms ?? roles.data?._permissions ?? boo?._permissions;
  const canConfigure = rolePerms?.canConfigure === true;
  const canManage = perms?.canManageMembers === true;
  const canToggle = channels.data?._permissions?.canToggle !== false && canManage;
  const groups = customGroups(roles.data?.roles ?? {});
  const chatPlatforms = (platforms.data?.platforms ?? []).filter((p) => p.platform !== "dashboard_chat" && p.enabled);
  const blockNewUsers = channels.data?.blockNewUsers ?? false;

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [pending, setPending] = useState<Pending | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [invite, setInvite] = useState<InvitePreset | null>(null);
  const [addingFromOrg, setAddingFromOrg] = useState(false);
  const [handingOver, setHandingOver] = useState(false);
  const [taking, setTaking] = useState(false);
  const [linkPicker, setLinkPicker] = useState<LinkPicker | null>(null);
  const [historyShown, setHistoryShown] = useState(HISTORY_STEP);
  const [addingFromChat, setAddingFromChat] = useState(false);
  const [paging, setPaging] = useState({ key: "", page: 1 });
  // ?group= picks the group in "What each group can do in chat"; the list
  // filter is this page's own.
  const [groupFilter, setGroupFilter] = useState<string | null>(null);
  const listRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const route = useSearch({ strict: false }) as PeopleSearch;
  const hash = useLocation({ select: (l) => l.hash });
  const tab = activePeopleTab(route, hash);
  // Replace history so flipping tabs doesn't stack back-button entries.
  const setTab = (t: PeopleTab) =>
    void navigate({
      to: "/orgs/$orgId/containers/$containerId/people",
      params: { orgId: params.orgId ?? "", containerId },
      search: t === "people" ? {} : t === "groups" && route.group ? { tab: t, group: route.group } : { tab: t },
      replace: true,
    });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: peopleKey(containerId) });
    queryClient.invalidateQueries({ queryKey: ["channels", containerId] });
    queryClient.invalidateQueries({ queryKey: ["boo-members", containerId] });
    queryClient.invalidateQueries({ queryKey: ["boo-access-events", containerId] });
    queryClient.invalidateQueries({ queryKey: ["containers"] });
  };

  const update = useMutation({
    mutationFn: (vars: { person: PersonRow; patch: PersonUpdate; to?: LevelKey }) =>
      api.containers.people.update(containerId, vars.person.personId, vars.patch),
    onMutate: (vars) => setBusyId(vars.person.personId),
    onSettled: () => setBusyId(null),
    onSuccess: (_d, vars) => {
      refresh();
      setPending(null);
      const name = displayName(vars.person);
      if (vars.to === "blocked") {
        const before = levelOf(vars.person);
        toast.success(`${name} is blocked. If they write to Boo, it sends its not-available reply.`,
          {
            action: {
              label: "Undo",
              onClick: () => update.mutate({ person: { ...vars.person, blocked: true }, patch: { blocked: false }, to: before }),
            },
          },
        );
      } else if (vars.to) {
        toast.success(`${name} is now ${levelLabel(vars.to)}.`);
      } else if (vars.patch.viewAllSpend !== undefined) {
        toast.success(
          vars.patch.viewAllSpend ? `${name} can now see everyone's usage costs.` : `${name} can no longer see everyone's usage costs.`,
        );
      }
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "That didn't save. Try again."),
  });

  const link = useMutation({
    mutationFn: async (vars: { into: string; handles: HandleRef[]; remove?: boolean }) => {
      let done = 0;
      for (const h of vars.handles) {
        const ref: HandleRef = { provider: h.provider, workspace: h.workspace, providerUserId: h.providerUserId };
        try {
          if (vars.remove) await api.containers.people.unlink(containerId, vars.into, ref);
          else await api.containers.people.link(containerId, vars.into, ref);
        } catch (err) {
          throw done > 0 ? new PartlyLinked(done, vars.handles.length, err) : err;
        }
        done += 1;
      }
    },
    onError: refresh,
    onSuccess: (_d, vars) => {
      refresh();
      setPending(null);
      toast.success(vars.remove ? "Separated. Boo now treats them as different people." : "Merged. Boo now treats them as one person.");
    },
  });

  const membership = useMutation({
    mutationFn: (vars: { person: PersonRow; group: string; add: boolean }) =>
      vars.add
        ? api.containers.people.addGroup(containerId, vars.person.personId, vars.group)
        : api.containers.people.removeGroup(containerId, vars.person.personId, vars.group),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: peopleKey(containerId) });
      const prev = queryClient.getQueryData<PeopleResponse>(peopleKey(containerId));
      queryClient.setQueryData<PeopleResponse>(peopleKey(containerId), (old) =>
        old
          ? {
              ...old,
              data: old.data.map((p) => {
                if (p.personId !== vars.person.personId) return p;
                const rest = p.groups.filter((g) => g !== vars.group);
                return { ...p, groups: vars.add ? [...rest, vars.group] : rest };
              }),
            }
          : old,
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(peopleKey(containerId), ctx.prev);
      toast.error(err instanceof Error ? err.message : "That didn't save. Try again.");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: peopleKey(containerId) }),
  });

  const removeMember = useMutation({
    mutationFn: (person: PersonRow) => api.containers.members.remove(containerId, person.personId),
    onSuccess: () => {
      refresh();
      setPending(null);
    },
  });

  const rows = people.data?.data ?? [];
  const allInvites = people.data?.pendingInvites ?? [];
  const counts = {
    all: rows.length + allInvites.length,
    dashboard: rows.filter((p) => p.kind === "managed").length + allInvites.length,
    chat: rows.filter((p) => p.kind === "chat_only").length,
    blocked: rows.filter((p) => p.blocked).length,
    invited: allInvites.length,
  };
  const visible = rows
    .filter((p) => {
      if (filter === "invited") return false;
      if (filter === "dashboard" && p.kind !== "managed") return false;
      if (filter === "chat" && p.kind !== "chat_only") return false;
      if (filter === "blocked" && !p.blocked) return false;
      if (groupFilter !== null && !chatGroups(p).includes(groupFilter)) return false;
      return matchesQuery(personSearchText(p), search);
    })
    .sort(
      (a, b) =>
        Number(b.personId === myUserId) - Number(a.personId === myUserId) ||
        displayName(a).localeCompare(displayName(b)),
    );

  const openPending = (next: Pending) => {
    link.reset();
    removeMember.reset();
    setPending(next);
  };

  const groupEditable = (p: PersonRow) =>
    groups.length > 0 &&
    (p.kind === "managed" && (p.booRole === "owner" || p.booRole === "admin")
      ? perms?.canManageAdmins === true
      : perms?.canManageMembers === true);

  const pickLevel = (person: PersonRow, to: LevelKey) => {
    const patch = levelPatch(person, to);
    if (Object.keys(patch).length === 0) return;
    if (person.kind === "managed") openPending({ kind: "level", person, to });
    else update.mutate({ person, patch, to });
  };

  const unlinkedAccounts = rows.filter((p) => p.kind === "chat_only" && p.handles.some((h) => LINKABLE_PROVIDERS.has(h.provider)));
  const dashboardPeople = rows.filter((p) => p.kind === "managed");
  const pickAccountsFor = (person: PersonRow) =>
    setLinkPicker({
      mode: "accounts",
      subject: person,
      options: unlinkedAccounts,
      likely: new Set(unlinkedAccounts.filter((a) => likelyMatch(a, rows)?.personId === person.personId).map((a) => a.personId)),
    });
  const pickPersonFor = (account: PersonRow) => {
    const likely = likelyMatch(account, rows);
    setLinkPicker({ mode: "person", subject: account, options: dashboardPeople, likely: new Set(likely ? [likely.personId] : []) });
  };
  const inChat: ChatSummary = (base, groups) =>
    roles.data ? chatSummary(roles.data.roles, roles.data.features, base, groups) : "";
  const featureTitle = (id: string) => {
    const f = roles.data?.features.find((x) => x.id === id);
    return f ? featureCopy(f).title : id.replace(/_/g, " ");
  };
  const pendingInvites = allInvites.filter(
    (inv) =>
      groupFilter === null &&
      filter !== "chat" &&
      filter !== "blocked" &&
      matchesQuery(`${inv.email} Invited ${ROLE_LABEL[inv.booRole]}`, search),
  );
  // Invites lead so they stay on page 1.
  const items: ListItem[] = [
    ...pendingInvites.map((invite): ListItem => ({ type: "invite", invite })),
    ...visible.map((person): ListItem => ({ type: "person", person })),
  ];
  const listKey = JSON.stringify([search, filter, groupFilter]);
  // Any change to the search or a filter starts again at page 1, even one
  // that comes back to an earlier search.
  if (paging.key !== listKey) setPaging({ key: listKey, page: 1 });
  const shown = pageOf(items, paging.key === listKey ? paging.page : 1);
  const goToPage = (n: number) => {
    setPaging({ key: listKey, page: n });
    const top = listRef.current?.getBoundingClientRect().top;
    if (top !== undefined && top < 0) listRef.current?.scrollIntoView({ block: "start" });
  };
  const showEveryone = () => {
    setSearch("");
    setFilter("all");
    setGroupFilter(null);
  };
  const removeInvite = useMutation({
    mutationFn: (inv: PendingInvite) => api.containers.people.removeInvite(containerId, inv.invitationId),
    onSuccess: (_d, inv) => {
      refresh();
      toast.success(`${inv.email} won't get access to this Boo when they join.`);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "That didn't save. Try again."),
  });
  const cancelInvite = useMutation({
    mutationFn: async (inv: PendingInvite) => {
      const res = await authClient.organization.cancelInvitation({ invitationId: inv.invitationId });
      if (res.error) throw new Error(res.error.message ?? "That didn't cancel. Try again.");
    },
    onSuccess: (_d, inv) => {
      refresh();
      toast.success(`Canceled the invite to ${inv.email}.`);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "That didn't cancel. Try again."),
  });

  const canHandOver =
    perms?.canManageAdmins || boo?._permissions?.canTakeOwnership || orgPerms.data?._permissions?.canOverrideAccess;
  const canTake = boo?._permissions?.canTakeOwnership === true && perms?.canManageAdmins !== true;
  const addable = chatPlatforms.filter((p) => p.directorySupported);
  const eventList = events.data?.data ?? [];
  const history = collapseHistory(eventList.map((e) => ({ id: e.id, text: describeAccessEvent(e, null, featureTitle), reason: e.reason, at: e.createdAt })));

  const canAddFromChat = canToggle && addable.length > 0;
  const tabItems: TabStripItem<PeopleTab>[] = [
    { value: "people", label: "People", count: people.data ? counts.all : null },
    { value: "groups", label: "Groups", count: roles.data ? groups.length + 2 : null },
    { value: "settings", label: "Settings" },
    { value: "history", label: "History" },
  ];

  const renderPerson = (person: PersonRow) => {
    const level = levelOf(person);
    const isMe = person.personId === myUserId;
    const editable = canChangeLevel(person, perms, myUserId);
    const canRemove =
      person.kind === "managed" &&
      (isMe ||
        (person.booRole === "member" ? perms?.canManageMembers === true : perms?.canManageAdmins === true));
    const canSeparate = person.kind === "managed" && canManage;
    const chatHandles = person.handles.filter((h) => h.provider !== "dashboard_chat");
    const linkedHandles = chatHandles.filter(
      (h) => (h.source === "manual" || h.source === "install") && LINKABLE_PROVIDERS.has(h.provider),
    );
    const invitable = person.kind === "chat_only" && canManage && !!params.orgId;
    const canLinkAccounts = person.kind === "managed" && canManage;
    const canLinkToPerson = person.kind === "chat_only" && canManage && person.handles.some((h) => LINKABLE_PROVIDERS.has(h.provider));
    const menu = invitable || canLinkAccounts || canLinkToPerson || (canSeparate && linkedHandles.length > 0) || canRemove || isSuperAdmin;
    return (
      <li
        key={person.personId}
        className="grid grid-cols-[minmax(0,1fr)_2rem] items-center gap-x-3 gap-y-2 px-4 py-3 sm:grid-cols-[minmax(0,1.2fr)_10rem_minmax(0,1fr)_6rem_2rem] sm:gap-x-4 sm:gap-y-1"
      >
        <div className="order-1 flex min-w-0 items-center gap-3">
          <Avatar className="size-8 shrink-0">
            <AvatarFallback className={cn("text-[11px]", person.blocked && "opacity-60")}>
              {rawIdName(person) ? "?" : initials(person.name, person.email ?? "?")}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col">
            <span
              className={cn("truncate text-sm font-medium", person.blocked && "text-muted-foreground")}
              title={rawIdName(person) ?? undefined}
            >
              {displayName(person)}
              {isMe && <span className="font-normal text-muted-foreground"> (you)</span>}
            </span>
            <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
              <PlatformIcons handles={chatHandles} />
              <span className="truncate">
                {person.kind === "managed"
                  ? (person.email ?? "Dashboard")
                  : [person.email, "Chat only"].filter(Boolean).join(" · ")}
              </span>
              <span className="shrink-0 tabular-nums sm:hidden">· {lastActive(person.lastActive)}</span>
            </span>
            <SpendLine
              personKey={personKeyOf(person)}
              lines={spendLineOf}
              orgId={params.orgId}
              canChange={spendLines.data?.canChangeInBilling === true}
            />
          </div>
        </div>
        <div className="order-3 col-span-full pl-11 sm:order-2 sm:col-span-1 sm:pl-0">
          <LevelPicker
            value={level}
            options={levelOptions(person, perms)}
            editable={editable}
            busy={busyId === person.personId}
            personName={displayName(person)}
            onPick={(to) => pickLevel(person, to)}
            onInviteAsAdmin={
              invitable && perms?.canManageAdmins
                ? () =>
                    setInvite({
                      email: person.email ?? undefined,
                      name: displayName(person),
                      boo: { containerId, role: "admin" },
                    })
                : undefined
            }
          />
        </div>
        <div className={cn("order-4 col-span-full pl-11 sm:order-3 sm:col-span-1 sm:pl-0", chatGroups(person).length === 0 && !(editable || groupEditable(person)) && "hidden sm:block")}>
          <GroupChips
            value={person.groups}
            kept={person.keptGroups ?? []}
            all={groups}
            editable={groupEditable(person)}
            personName={displayName(person)}
            onAdd={(g) => membership.mutate({ person, group: g, add: true })}
            onRemove={(g) => membership.mutate({ person, group: g, add: false })}
          />
        </div>
        <span className="order-5 hidden text-sm tabular-nums text-muted-foreground sm:order-4 sm:block">
          {lastActive(person.lastActive)}
        </span>
        <div className="order-2 flex justify-end sm:order-5">
          {menu && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button size="icon-sm" variant="ghost" aria-label={`More for ${displayName(person)}`}>
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                {invitable && (
                  <DropdownMenuItem
                    onSelect={() =>
                      setInvite({
                        email: person.email ?? undefined,
                        name: displayName(person),
                        boo: { containerId, role: "member" },
                      })
                    }
                  >
                    Invite to dashboard
                  </DropdownMenuItem>
                )}
                {canLinkAccounts && (
                  <DropdownMenuItem onSelect={() => pickAccountsFor(person)}>Link a chat account…</DropdownMenuItem>
                )}
                {canLinkToPerson && (
                  <DropdownMenuItem onSelect={() => pickPersonFor(person)}>Link to a dashboard person…</DropdownMenuItem>
                )}
                {canSeparate &&
                  linkedHandles.map((h) => (
                    <DropdownMenuItem
                      key={`${h.provider}:${h.providerUserId}`}
                      onSelect={() => openPending({ kind: "separate", person, handle: h })}
                    >
                      Separate {platformLabel(h.provider)} account
                    </DropdownMenuItem>
                  ))}
                {isSuperAdmin && (
                  <DropdownMenuCheckboxItem
                    checked={person.viewAllSpend === true}
                    onCheckedChange={(checked) =>
                      update.mutate({ person, patch: { viewAllSpend: checked === true } })
                    }
                  >
                    Can see everyone&apos;s usage costs
                  </DropdownMenuCheckboxItem>
                )}
                {canRemove && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" onSelect={() => openPending({ kind: "remove", person })}>
                      {isMe ? "Leave this Boo" : "Remove from dashboard"}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </li>
    );
  };

  const peopleTab = (
    <section ref={listRef} className="flex scroll-mt-6 flex-col gap-3" aria-label="People">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, email or group"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8"
            aria-label="Search people"
          />
        </div>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Show">
          {(Object.keys(FILTER_LABEL) as Filter[]).map((f) =>
            f !== "all" && f !== filter && counts[f] === 0 ? null : (
              <Button
                key={f}
                size="sm"
                variant={filter === f ? "secondary" : "ghost"}
                aria-pressed={filter === f}
                onClick={() => setFilter(f)}
                className="tabular-nums"
              >
                {FILTER_LABEL[f]}
                <span className="text-muted-foreground">{counts[f]}</span>
              </Button>
            ),
          )}
          {groupFilter !== null && (
            <Button size="sm" variant="secondary" onClick={() => setGroupFilter(null)} aria-label={`Stop showing only ${groupLabel(groupFilter)}`}>
              Only {groupLabel(groupFilter)}
              <X />
            </Button>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {(canManage || canAddFromChat) && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button size="sm">
                  <UserPlus />
                  Add people
                  <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                {canAddFromChat && (
                  <DropdownMenuItem onSelect={() => setAddingFromChat(true)} className="items-start">
                    <MessageSquare className="mt-0.5" />
                    <AddOption
                      title={`From ${addable.length === 1 ? PLATFORM_LABEL[addable[0].platform] : "chat"}`}
                      hint="Choose how Boo treats them before they first write"
                    />
                  </DropdownMenuItem>
                )}
                {canManage && (
                  <DropdownMenuItem onSelect={() => setAddingFromOrg(true)} className="items-start">
                    <Users className="mt-0.5" />
                    <AddOption title="From your organization" hint="People who already use the dashboard" />
                  </DropdownMenuItem>
                )}
                {canManage && params.orgId && (
                  <DropdownMenuItem onSelect={() => setInvite({ boo: { containerId, role: "member" } })} className="items-start">
                    <Mail className="mt-0.5" />
                    <AddOption title="Invite to dashboard" hint="Emails an invite to see or manage this Boo" />
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {(canHandOver || canTake) && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button size="icon-sm" variant="ghost" aria-label="More">
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canHandOver && <DropdownMenuItem onSelect={() => setHandingOver(true)}>Hand over this Boo</DropdownMenuItem>}
                {canTake && <DropdownMenuItem onSelect={() => setTaking(true)}>Take ownership</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {people.isLoading ? (
        <ListSkeleton />
      ) : people.isError ? (
        <InlineError error={people.error} />
      ) : rows.length === 0 && allInvites.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-12 text-center">
          <Users className="size-7 text-muted-foreground" />
          <p className="text-sm font-medium">No one yet</p>
          <p className="max-w-sm text-sm text-muted-foreground text-pretty">
            People show up here the first time they talk to Boo, or when you invite them to the dashboard.
          </p>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed py-10 text-center">
          <p className="text-sm text-muted-foreground">No one matches.</p>
          <Button variant="link" size="sm" onClick={showEveryone}>
            Show everyone
          </Button>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border bg-card">
            <div className="hidden grid-cols-[minmax(0,1.2fr)_10rem_minmax(0,1fr)_6rem_2rem] items-center gap-4 border-b bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground sm:grid">
              <span>Person</span>
              <span>Access</span>
              <span>Groups</span>
              <span>Last active</span>
              <span />
            </div>
            <ul className="divide-y">
              {shown.items.map((item) =>
                item.type === "person" ? (
                  renderPerson(item.person)
                ) : (
                  <InviteRow
                    key={item.invite.invitationId}
                    invite={item.invite}
                    canRemove={item.invite.booRole === "member" ? canManage : perms?.canManageAdmins === true}
                    canCancel={orgRole.canManage}
                    busy={removeInvite.isPending || cancelInvite.isPending}
                    onRemove={() => removeInvite.mutate(item.invite)}
                    onCancel={() => cancelInvite.mutate(item.invite)}
                  />
                ),
              )}
            </ul>
          </div>
          <Pager page={shown} onPage={goToPage} />
        </>
      )}
    </section>
  );

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-auto">
      <div className="flex flex-col gap-6 p-6">
        <PageHeader
          title="People"
          subtitle="Everyone who talks to Boo shows up here. Their group decides what they can use in chat. Invite someone to the dashboard only if they should see or manage this Boo."
        />

        {people.data?.ownerless && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200">
            <span>This Boo has no owner. Nobody can add admins or owners until it has one.</span>
            {canTake && (
              <Button size="sm" variant="outline" onClick={() => setTaking(true)}>
                Take ownership
              </Button>
            )}
          </div>
        )}

        {roles.data && (
          <UpgradeNotices containerId={containerId} data={roles.data} isOwner={rolePerms?.canManageAdmins === true} />
        )}

        <TabStrip tabs={tabItems} active={tab} onSelect={setTab} label="People sections" />

        {tab === "people" && peopleTab}

        {tab === "groups" &&
          (roles.data ? (
            <GroupSettings
              containerId={containerId}
              data={roles.data}
              people={people.data ? rows : null}
              canEdit={canConfigure}
              onShowPeople={(g) => {
                showEveryone();
                setGroupFilter(g);
                setTab("people");
              }}
              onDeleted={(g) => setGroupFilter((f) => (f === g ? null : f))}
            />
          ) : roles.isError ? (
            <InlineError error={roles.error} />
          ) : (
            <Skeleton className="h-96 w-full rounded-lg" />
          ))}

        {tab === "settings" && (
          <div className="flex flex-col gap-8">
            <WhenBooAnswers containerId={containerId} />
            {roles.data ? (
              <AdvancedSettings containerId={containerId} data={roles.data} canEdit={canConfigure} />
            ) : roles.isError ? (
              <InlineError error={roles.error} />
            ) : (
              <Skeleton className="h-8 w-40" />
            )}
          </div>
        )}

        {tab === "history" &&
          (events.isLoading ? (
            <ListSkeleton />
          ) : events.isError ? (
            <InlineError error={events.error} />
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed py-10 text-center">
              <p className="text-sm font-medium">No changes yet</p>
              <p className="max-w-sm text-sm text-muted-foreground text-pretty">
                When someone changes who can use this Boo or what they can do, it shows up here.
              </p>
            </div>
          ) : (
            <section className="flex flex-col gap-3" aria-label="History">
              <ol className="flex flex-col divide-y rounded-lg border">
                {history.slice(0, historyShown).map((h) => (
                  <li key={h.id} className="flex flex-col gap-0.5 px-4 py-2.5">
                    <span className="text-sm text-pretty">
                      {h.text}
                      {h.times > 1 && <span className="text-muted-foreground tabular-nums"> ({h.times} times)</span>}
                    </span>
                    {h.reason && <span className="text-sm text-muted-foreground">Reason: “{h.reason}”</span>}
                    <span className="text-xs tabular-nums text-muted-foreground">{formatWhen(h.at)}</span>
                  </li>
                ))}
              </ol>
              {history.length > historyShown && (
                <Button variant="outline" size="sm" className="self-start" onClick={() => setHistoryShown((n) => n + HISTORY_STEP)}>
                  Show {Math.min(HISTORY_STEP, history.length - historyShown)} more
                </Button>
              )}
            </section>
          ))}
      </div>

      {canAddFromChat && (
        <ChannelDirectoryDialog
          containerId={containerId}
          kind="user"
          platforms={addable}
          defaultBlocked={!blockNewUsers}
          blockNewUsers={blockNewUsers}
          open={addingFromChat}
          onOpenChange={setAddingFromChat}
        />
      )}
      <AddFromOrgDialog containerId={containerId} perms={perms} open={addingFromOrg} onClose={() => setAddingFromOrg(false)} />
      <LinkPickerDialog
        picker={linkPicker}
        onClose={() => setLinkPicker(null)}
        onPick={(chosen) => {
          if (!linkPicker) return;
          const next: Pending =
            linkPicker.mode === "accounts"
              ? { kind: "merge", accounts: chosen, into: linkPicker.subject }
              : { kind: "merge", accounts: [linkPicker.subject], into: chosen[0] };
          setLinkPicker(null);
          openPending(next);
        }}
      />
      {params.orgId && (
        <InviteDialog
          open={invite !== null}
          onClose={() => setInvite(null)}
          organizationId={params.orgId}
          preset={invite ?? undefined}
        />
      )}
      <PendingDialog
        pending={pending}
        myUserId={myUserId}
        inChat={inChat}
        busy={update.isPending || link.isPending || removeMember.isPending}
        error={pending?.kind === "remove" ? removeMember.error : pending?.kind === "merge" || pending?.kind === "separate" ? link.error : null}
        onCancel={() => {
          link.reset();
          removeMember.reset();
          setPending(null);
        }}
        onConfirm={(p) => {
          if (p.kind === "level") update.mutate({ person: p.person, patch: levelPatch(p.person, p.to), to: p.to });
          if (p.kind === "merge") {
            link.mutate({ into: p.into.personId, handles: p.accounts.flatMap((a) => a.handles.filter((h) => LINKABLE_PROVIDERS.has(h.provider))) });
          }
          if (p.kind === "separate") link.mutate({ into: p.person.personId, handles: [p.handle], remove: true });
          if (p.kind === "remove") removeMember.mutate(p.person);
        }}
      />
      {boo && <TakeOwnershipDialog containerId={containerId} booName={boo.name} open={taking} onOpenChange={setTaking} />}
      {handingOver && <HandoverSheet booId={containerId} onClose={() => setHandingOver(false)} />}
    </div>
  );
}

type HistoryLine = { id: string; text: string; reason: string | null; at: string; times: number };

function collapseHistory(lines: Omit<HistoryLine, "times">[]): HistoryLine[] {
  const out: HistoryLine[] = [];
  for (const line of lines) {
    const prev = out[out.length - 1];
    if (prev && prev.text === line.text && prev.reason === line.reason) prev.times += 1;
    else out.push({ ...line, times: 1 });
  }
  return out;
}

function InviteRow({
  invite,
  canRemove,
  canCancel,
  busy,
  onRemove,
  onCancel,
}: {
  invite: PendingInvite;
  canRemove: boolean;
  canCancel: boolean;
  busy: boolean;
  onRemove: () => void;
  onCancel: () => void;
}) {
  const level = ROLE_LABEL[invite.booRole];
  const by = invite.invitedBy?.name || invite.invitedBy?.email;
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_2rem] items-center gap-x-3 gap-y-2 px-4 py-3 sm:grid-cols-[minmax(0,1.2fr)_10rem_minmax(0,1fr)_6rem_2rem] sm:gap-x-4 sm:gap-y-1">
      <div className="order-1 flex min-w-0 items-center gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-dashed text-muted-foreground">
          <Mail className="size-3.5" />
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium">{invite.email}</span>
          <span className="truncate text-xs text-muted-foreground">
            Invited{by ? ` by ${by}` : ""} · waiting for them to join
          </span>
        </div>
      </div>
      <span className="order-3 col-span-full pl-11 text-sm text-muted-foreground sm:order-2 sm:col-span-1 sm:pl-1">{level}</span>
      <span className="hidden sm:order-3 sm:block" />
      <span className="order-4 hidden text-sm text-muted-foreground sm:order-4 sm:block">Not yet</span>
      <div className="order-2 flex justify-end sm:order-5">
        {(canRemove || canCancel) && (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button size="icon-sm" variant="ghost" aria-label={`More for the invite to ${invite.email}`} disabled={busy}>
                <EllipsisVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {canRemove && <DropdownMenuItem onSelect={onRemove}>Remove from this Boo</DropdownMenuItem>}
              {canCancel && (
                <DropdownMenuItem variant="destructive" onSelect={onCancel}>
                  Cancel the whole invite
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </li>
  );
}

function lastActive(iso: string | null): string {
  return iso ? relativeIso(iso) : "Not yet";
}

function PendingDialog({
  pending,
  myUserId,
  inChat,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  pending: Pending | null;
  myUserId: string | undefined;
  inChat: ChatSummary;
  busy: boolean;
  error: Error | null;
  onCancel: () => void;
  onConfirm: (p: Pending) => void;
}) {
  const copy = pending ? pendingCopy(pending, myUserId, inChat) : null;
  return (
    <ConfirmDialog
      open={pending !== null}
      onOpenChange={(open) => !open && onCancel()}
      title={copy?.title ?? ""}
      description={copy?.description ?? ""}
      confirmLabel={copy?.confirm}
      variant={copy?.destructive ? "destructive" : "default"}
      loading={busy}
      onConfirm={() => pending && onConfirm(pending)}
    >
      {pending && error && (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive text-pretty">
          {problemText(pending, error)}
        </p>
      )}
    </ConfirmDialog>
  );
}

class PartlyLinked extends Error {
  readonly done: number;
  readonly total: number;
  readonly reason: unknown;

  constructor(done: number, total: number, reason: unknown) {
    super(reason instanceof Error ? reason.message : "That didn't save.");
    this.done = done;
    this.total = total;
    this.reason = reason;
  }
}

function problemText(p: Pending, error: unknown): string {
  if (error instanceof PartlyLinked) {
    const into = p.kind === "merge" ? ` to ${displayName(p.into)}` : "";
    return `The merge only partly happened: ${error.done} of ${error.total} chat accounts are now linked${into}. ${problemText(p, error.reason)}`;
  }
  const handle = p.kind === "merge" ? p.accounts[0]?.handles[0] : p.kind === "separate" ? p.handle : undefined;
  const account = handle && LINKABLE_PROVIDERS.has(handle.provider) ? `That ${platformLabel(handle.provider)} account` : "That chat account";
  if (error instanceof ApiError) {
    if (error.code === "handle_taken") return `${account} already belongs to someone else here.`;
  }
  return (error instanceof Error && error.message) || "That didn't save. Try again.";
}

function pendingCopy(
  p: Pending,
  myUserId: string | undefined,
  inChat: ChatSummary,
): { title: string; description: string; confirm: string; destructive?: boolean } {
  if (p.kind === "merge") return mergeCopy(p.accounts, p.into);
  const name = displayName(p.person);
  const isMe = p.person.personId === myUserId;
  switch (p.kind) {
    case "level": {
      const label = levelLabel(p.to);
      return {
        title: p.to === "blocked" ? `Block ${name}?` : `Change ${name} to ${label}?`,
        description: consequence(p.person, p.to, isMe, inChat),
        confirm: p.to === "blocked" ? "Block" : `Change to ${label}`,
        destructive: p.to === "blocked",
      };
    }
    case "separate": {
      const platform = platformLabel(p.handle.provider);
      return {
        title: `Separate this ${platform} account from ${name}?`,
        description: `Boo will treat the ${platform} account as a different person, at the Standard level, until you merge them again.`,
        confirm: "Separate",
      };
    }
    case "remove":
      return isMe
        ? {
            title: "Leave this Boo?",
            description: "You won't be able to open this Boo in the dashboard until someone adds you back. Boo still answers you in chat.",
            confirm: "Leave",
            destructive: true,
          }
        : {
            title: `Remove ${name} from the dashboard?`,
            description: `${name} won't be able to open this Boo in the dashboard. Boo still answers them in chat, with the Standard level plus their groups.`,
            confirm: "Remove",
            destructive: true,
          };
  }
}

function mergeCopy(accounts: PersonRow[], target: PersonRow): { title: string; description: string; confirm: string } {
  const into = displayName(target);
  const first = into.split(/\s+/)[0] ?? into;
  const level = levelOf(target);
  const elevated = level === "owner" || level === "admin";
  const one = accounts.length === 1 ? accounts[0] : null;
  const platform = platformLabel(one?.handles.find((h) => LINKABLE_PROVIDERS.has(h.provider))?.provider ?? "");
  const what = one ? `this ${platform} account` : "these chat accounts";
  const whoAll = one ? `the ${platform} account “${displayName(one)}”` : `${accounts.length} chat accounts`;
  return {
    title: `Link ${whoAll} to ${into}${target.email ? ` (${target.email})` : ""}?`,
    description: elevated
      ? `Boo will treat them as one person. ${first} is ${levelLabel(level)} here, so ${what} will be able to do everything ${level === "owner" ? "an Owner" : "an Admin"} can.`
      : `Boo will treat them as one person. ${first} keeps ${levelLabel(level)}.`,
    confirm: one ? "Link" : `Link ${accounts.length} accounts`,
  };
}

function ListSkeleton() {
  return (
    <div className="flex flex-col divide-y rounded-lg border">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <Skeleton className="size-8 rounded-full" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-8 w-36" />
        </div>
      ))}
    </div>
  );
}

const SPEND_TONE = {
  "": "text-muted-foreground",
  warn: "text-amber-700 dark:text-amber-400",
  crit: "text-destructive",
} as const;

// Read-only line; the limit itself is changed on the Usage page's Spending limits tab. Hidden for
// people without a limit of their own or the default.
function SpendLine({
  personKey,
  lines,
  orgId,
  canChange,
}: {
  personKey: string | null;
  lines: Map<string, SpendPeopleLine | null>;
  orgId: string | undefined;
  canChange: boolean;
}) {
  const line = personKey ? lines.get(personKey) : null;
  if (!line) return null;
  const cls = cn("mt-0.5 w-fit text-xs tabular-nums", SPEND_TONE[peopleLineTone(line)]);
  // Editors click the line itself to change it on the Spending limits tab.
  if (canChange && orgId) {
    return (
      <Link to="/orgs/$orgId/usage" params={{ orgId }} search={{ tab: "limits" }} hash="spending-limits" className={cn(cls, "underline-offset-4 hover:underline")}>
        {peopleLineText(line)}
      </Link>
    );
  }
  return <span className={cls}>{peopleLineText(line)}</span>;
}

function AddOption({ title, hint }: { title: string; hint: string }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span>{title}</span>
      <span className="text-xs text-muted-foreground text-pretty">{hint}</span>
    </span>
  );
}

// The billing tables' pager.
function Pager({ page, onPage }: { page: Page<unknown>; onPage: (n: number) => void }) {
  if (page.pages <= 1) return null;
  return (
    <div className="flex items-center justify-between text-sm text-muted-foreground">
      <span className="tabular-nums">
        {page.from}–{page.to} of {page.total}
      </span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => onPage(page.page - 1)} disabled={page.page === 1}>
          Previous
        </Button>
        <Button variant="outline" size="sm" onClick={() => onPage(page.page + 1)} disabled={page.page === page.pages}>
          Next
        </Button>
      </div>
    </div>
  );
}
